/**
 * lib/admissionFormDefaults.ts
 * ------------------------------------------------------------------
 * Default §7 boilerplate for the printed application form (fee
 * statement, document lists, and the Conditions of Admission).
 *
 * These are the SEED / FALLBACK values. The live, editable copy lives
 * in Firestore at `reportSchoolSettings/admissionFormText` and is
 * managed by admins in Admissions → Settings. The print PDF reads the
 * live doc and falls back to these defaults when it is missing/empty,
 * so the form is never blank.
 *
 * ⚠️ DRAFT — the Conditions of Admission below were transcribed from a
 * photo of the paper form and NEED VERIFICATION against the school's
 * official master copy (clauses 11 & 12 especially). Because the text
 * is now editable, the school reviews and corrects it in the UI without
 * a code change.
 * ------------------------------------------------------------------
 */
import type { AdmissionFormText } from '@/types';

export const DEFAULT_FEE_STATEMENT =
  'A non-refundable application fee of P500.00 and a registration fee of P300.00 are payable. ' +
  'A place is confirmed only once the application is approved and both fees have been received in full. ' +
  'The admissions office will confirm the current amounts and payment details.';

export const DEFAULT_DOCUMENTS_REQUIRED: string[] = [
  'Certified copy of the pupil’s birth certificate',
  'Certified copy of the pupil’s passport / Omang (ID)',
  'Certified copies of parents’ / guardian’s passports / Omang (ID)',
  'Residence / work permit (non-citizens)',
  'Latest school report / academic records',
  'Transfer / clearance letter from the previous school',
  'Two (2) recent passport-size photographs',
];

export const DEFAULT_DOCUMENTS_RECEIVED: string[] = [
  'Birth certificate',
  'Passport / Omang (ID)',
  'Parents’ / guardian’s ID',
  'Residence / work permit',
  'Latest school report',
  'Passport photographs',
];

/** DRAFT — transcribed from the paper form; verify against the master. */
export const DEFAULT_CONDITIONS: string[] = [
  'The student shall follow all school rules and regulations as laid down in the prospectus and communicated to students and parents from time to time, see student code of conduct document.',
  "Students will not be admitted to class unless school fees have been paid. Refer to the School Fees Policy for payment due dates of term's fees.",
  'Cheques returned by the bank will incur a handling charge of P300.00.',
  'Any outstanding amount of fees after a final deadline incurs a 10% surcharge.',
  'A development fee and school fees should be paid on acceptance on admission at this school.',
  "A term's written notice is required for your child to withdraw from Livingstone Kolobeng College or a term's school fees in full.",
  'On leaving the school, the student is required to complete a clearance form, which should be signed by the School authorities.',
  "Parents should come to school when required to discuss their children's progress.",
  'Parents will support the school in cases of disciplinary action against their child.',
  'I have read the student code of conduct and agree for my child to follow it in full and any amendment made from time to time by the school authorities.',
  "Photos taken during the class time and activities can be used for the school advertising. They are also expected to attend all parents-teachers' consultations.",
  "Parent(s)/Guardian(s) have the responsibility of supervising their student(s)' work at home.",
  'The school reserves the right to make decision in promoting or retaining a student.',
];

export const DEFAULT_ADMISSION_FORM_TEXT: AdmissionFormText = {
  feeStatement: DEFAULT_FEE_STATEMENT,
  documentsRequired: DEFAULT_DOCUMENTS_REQUIRED,
  documentsReceived: DEFAULT_DOCUMENTS_RECEIVED,
  conditions: DEFAULT_CONDITIONS,
};

/** Merge a (possibly partial / missing) config doc onto the defaults so
 *  the caller always has a complete, non-empty set to render. Empty
 *  arrays and blank strings fall back to the default. */
export function resolveAdmissionFormText(
  cfg?: Partial<AdmissionFormText> | null,
): AdmissionFormText {
  return {
    feeStatement: cfg?.feeStatement?.trim() || DEFAULT_FEE_STATEMENT,
    documentsRequired:
      cfg?.documentsRequired && cfg.documentsRequired.length
        ? cfg.documentsRequired
        : DEFAULT_DOCUMENTS_REQUIRED,
    documentsReceived:
      cfg?.documentsReceived && cfg.documentsReceived.length
        ? cfg.documentsReceived
        : DEFAULT_DOCUMENTS_RECEIVED,
    conditions:
      cfg?.conditions && cfg.conditions.length ? cfg.conditions : DEFAULT_CONDITIONS,
  };
}
